import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from '../supabaseClient';
import { getRuntimeConfig, resolveSystemUserId } from '../config/runtimeConfig';
import { FEATURED_TRIAL_TEMPLATES } from '../config/featuredTrials';

function StatusRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 text-xs sm:text-sm py-1.5 border-b border-slate-800 last:border-b-0">
      <div className="text-slate-400">{label}</div>
      <div className="text-slate-100 text-right break-all">{String(value ?? '—')}</div>
    </div>
  );
}

export default function Diagnostics() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [runningAction, setRunningAction] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [canView, setCanView] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [isAdminViewer, setIsAdminViewer] = useState(false);

  const localDebugEnabled = useMemo(() => {
    try {
      return localStorage.getItem('gweffit_debug') === '1';
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverflowY: html.style.overflowY,
      htmlPosition: html.style.position,
      htmlHeight: html.style.height,
      htmlTouchAction: html.style.touchAction,
      bodyOverflowY: body.style.overflowY,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyHeight: body.style.height,
      bodyTouchAction: body.style.touchAction,
      bodyWebkitOverflowScrolling: body.style.webkitOverflowScrolling,
      rootOverflowY: root?.style?.overflowY || '',
      rootOverflow: root?.style?.overflow || '',
      rootTouchAction: root?.style?.touchAction || '',
      rootWebkitOverflowScrolling: root?.style?.webkitOverflowScrolling || '',
      rootPosition: root?.style?.position || '',
      rootHeight: root?.style?.height || '',
    };

    // Defensive reset in case a prior modal/overlay leaves scroll styles in a bad state on iOS.
    html.style.overflow = 'auto';
    html.style.overflowY = 'auto';
    html.style.position = '';
    html.style.height = '';
    html.style.touchAction = 'auto';
    body.style.overflow = 'auto';
    body.style.overflowY = 'auto';
    body.style.position = '';
    body.style.height = '';
    body.style.touchAction = 'auto';
    body.style.webkitOverflowScrolling = 'touch';
    if (root) {
      root.style.overflow = 'auto';
      root.style.overflowY = 'auto';
      root.style.position = '';
      root.style.height = '';
      root.style.touchAction = 'auto';
      root.style.webkitOverflowScrolling = 'touch';
    }

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowY = prev.htmlOverflowY;
      html.style.position = prev.htmlPosition;
      html.style.height = prev.htmlHeight;
      html.style.touchAction = prev.htmlTouchAction;
      body.style.overflow = prev.bodyOverflow;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.position = prev.bodyPosition;
      body.style.height = prev.bodyHeight;
      body.style.touchAction = prev.bodyTouchAction;
      body.style.webkitOverflowScrolling = prev.bodyWebkitOverflowScrolling;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.overflowY = prev.rootOverflowY;
        root.style.position = prev.rootPosition;
        root.style.height = prev.rootHeight;
        root.style.touchAction = prev.rootTouchAction;
        root.style.webkitOverflowScrolling = prev.rootWebkitOverflowScrolling;
      }
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setActionMsg('');
    try {
      const { data: authData } = await supabase.auth.getUser();
      const authUserId = authData?.user?.id || null;
      const cfg = await getRuntimeConfig();

      let appInfo = { version: process.env.REACT_APP_VERSION || 'web-dev', build: process.env.REACT_APP_BUILD || 'web' };
      try {
        if (Capacitor.isNativePlatform()) {
          const info = await CapacitorApp.getInfo();
          appInfo = { version: info.version || 'native', build: info.build || 'native' };
        }
      } catch {}

      const runtimeAdmins = new Set((cfg?.adminAuthUserIds || []).map((v) => String(v)));
      const runtimeAdmin = authUserId ? runtimeAdmins.has(String(authUserId)) : false;
      const { data: adminRow } = authUserId
        ? await supabase.from('moderation_admins').select('auth_user_id').eq('auth_user_id', authUserId).eq('is_active', true).maybeSingle()
        : { data: null };
      const allowed = localDebugEnabled || runtimeAdmin || Boolean(adminRow?.auth_user_id);
      setIsAdminViewer(runtimeAdmin || Boolean(adminRow?.auth_user_id));
      setCanView(allowed);
      setAccessChecked(true);
      if (!allowed) {
        setSnapshot(null);
        setLoading(false);
        return;
      }

      const { data: appUser } = authUserId
        ? await supabase.from('users').select('id, auth_user_id, fcm_token_updated_at, push_enabled').eq('auth_user_id', authUserId).maybeSingle()
        : { data: null };

      const appUserId = appUser?.id || null;

      const [tokenCountRes, prefRes, outboxRes, pushQueueRes, coinEarningsRes, freezeRes] = await Promise.allSettled([
        appUserId
          ? supabase.from('user_fcm_tokens').select('user_id', { count: 'exact', head: true }).eq('user_id', appUserId).eq('is_active', true)
          : Promise.resolve({ count: 0 }),
        appUserId
          ? supabase.from('user_notification_preferences').select('notif_trial_summons, notif_trial_verdicts, notif_daily_decision, notif_streak').eq('user_id', appUserId).maybeSingle()
          : Promise.resolve({ data: null }),
        appUserId
          ? supabase
              .from('notification_outbox')
              .select('created_at, category, status, failure_reason, dedupe_key')
              .eq('user_id', appUserId)
              .order('created_at', { ascending: false })
              .limit(20)
          : Promise.resolve({ data: [] }),
        appUserId
          ? supabase.from('push_queue').select('created_at,status,dedupe_key,last_error,user_id').eq('user_id', appUserId).order('created_at', { ascending: false }).limit(20)
          : Promise.resolve({ data: [] }),
        appUserId
          ? supabase.from('daily_coin_earnings').select('earning_date,earned,created_at,updated_at').eq('user_id', appUserId).order('earning_date', { ascending: false }).limit(10)
          : Promise.resolve({ data: [] }),
        appUserId
          ? supabase.from('user_streak_freezes').select('freeze_count,updated_at').eq('user_id', appUserId).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      const tokenCount = tokenCountRes.status === 'fulfilled' ? Number(tokenCountRes.value?.count || 0) : 0;
      const prefsData = prefRes.status === 'fulfilled' ? prefRes.value?.data : null;
      const outboxData = outboxRes.status === 'fulfilled' ? (outboxRes.value?.data || []) : [];
      const pushQueueData = pushQueueRes.status === 'fulfilled' ? (pushQueueRes.value?.data || []) : [];
      const coinEarnData = coinEarningsRes.status === 'fulfilled' ? (coinEarningsRes.value?.data || []) : [];
      const freezeData = freezeRes.status === 'fulfilled' ? (freezeRes.value?.data || null) : null;

      const systemResolution = await resolveSystemUserId(supabase);
      let dbSystemUserId = null;
      if (cfg?.systemUsername) {
        const { data } = await supabase.from('users').select('id').eq('username', cfg.systemUsername).limit(1);
        dbSystemUserId = data?.[0]?.id || null;
      }

      setSnapshot({
        appInfo,
        envName: cfg?.envName || 'unknown',
        debug: !!cfg?.debug,
        authUserId,
        appUserId,
        systemConfiguredId: cfg?.systemUserId || null,
        systemUsername: cfg?.systemUsername || 'system',
        systemResolved: systemResolution,
        dbSystemUserId,
        pushPermission: Capacitor.isNativePlatform() ? 'native-managed' : (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'),
        pushEnabled: appUser?.push_enabled,
        activeTokenCount: tokenCount,
        fcmTokenUpdatedAt: appUser?.fcm_token_updated_at || null,
        prefs: prefsData || null,
        outbox: outboxData,
        pushQueue: pushQueueData,
        dailyCoinEarnings: coinEarnData,
        streakFreezes: freezeData?.freeze_count ?? 0,
        streakFreezesUpdatedAt: freezeData?.updated_at || null,
      });
    } catch (err) {
      console.error('[diag] load_failed', err);
      setActionMsg(`Load failed: ${err?.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [localDebugEnabled]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshTokens = useCallback(async () => {
    if (!snapshot?.appUserId) return;
    setRunningAction('refreshTokens');
    setActionMsg('');
    try {
      const { data: tokenRow } = await supabase
        .from('user_fcm_tokens')
        .select('fcm_token, device_id, platform')
        .eq('user_id', snapshot.appUserId)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const fallbackToken = tokenRow?.fcm_token || null;
      if (!fallbackToken) {
        setActionMsg('No active token found to refresh.');
        return;
      }

      const { data, error } = await supabase.rpc('register_user_fcm_token', {
        p_user_id: snapshot.appUserId,
        p_fcm_token: fallbackToken,
        p_device_id: tokenRow?.device_id || null,
        p_platform: tokenRow?.platform || (Capacitor.getPlatform?.() || 'web'),
      });
      if (error) throw error;
      setActionMsg(`Token refresh OK (${data?.registered_at || 'updated'}).`);
      await load();
    } catch (err) {
      console.error('[diag] refresh_tokens_failed', err);
      setActionMsg(`Token refresh failed: ${err?.message || String(err)}`);
    } finally {
      setRunningAction('');
    }
  }, [load, snapshot?.appUserId]);

  const createFeaturedTrial = useCallback(async () => {
    if (!snapshot?.authUserId || !snapshot?.appUserId) {
      setActionMsg('Sign in first.');
      return;
    }
    setRunningAction('createFeatured');
    setActionMsg('');
    try {
      const template = FEATURED_TRIAL_TEMPLATES[Math.floor(Math.random() * FEATURED_TRIAL_TEMPLATES.length)];
      const { data, error } = await supabase.functions.invoke('create-featured-trial', {
        body: { template_key: template?.key || null, seed_source: 'featured_v1' },
      });
      if (error) throw error;
      if (!data?.ok || !data?.trial_id) {
        throw new Error(data?.error || 'create_featured_trial_failed');
      }
      navigate(`/t/${data.trial_id}`);
    } catch (err) {
      console.error('[diag] create_featured_failed', err);
      setActionMsg(`Create featured failed: ${err?.message || String(err)}`);
    } finally {
      setRunningAction('');
    }
  }, [navigate, snapshot?.appUserId, snapshot?.authUserId]);

  const outboxRows = useMemo(() => snapshot?.outbox || [], [snapshot?.outbox]);
  const pushQueueRows = useMemo(() => snapshot?.pushQueue || [], [snapshot?.pushQueue]);
  const coinRows = useMemo(() => snapshot?.dailyCoinEarnings || [], [snapshot?.dailyCoinEarnings]);

  const copyDebugBundle = useCallback(async () => {
    if (!snapshot) return;
    try {
      const bundle = {
        generated_at: new Date().toISOString(),
        platform: Capacitor.getPlatform?.() || 'web',
        snapshot,
      };
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      setActionMsg('Debug bundle copied.');
    } catch (err) {
      setActionMsg(`Copy failed: ${err?.message || String(err)}`);
    }
  }, [snapshot]);

  const copyDebugSnapshot = useCallback(async () => {
    try {
      const runtime = {
        now: new Date().toISOString(),
        platform: Capacitor.getPlatform?.() || (Capacitor.isNativePlatform() ? 'native' : 'web'),
        route: {
          path: location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/'),
          search: location?.search || (typeof window !== 'undefined' ? window.location.search : ''),
        },
        auth_user_id: snapshot?.authUserId || null,
        app_user_id: snapshot?.appUserId || null,
        version: snapshot?.appInfo?.version || null,
        build: snapshot?.appInfo?.build || null,
        last_bootstrap_done: null,
      };
      await navigator.clipboard.writeText(JSON.stringify(runtime, null, 2));
      setActionMsg('Debug snapshot copied.');
    } catch (err) {
      setActionMsg(`Copy failed: ${err?.message || String(err)}`);
    }
  }, [location?.pathname, location?.search, snapshot]);

  const copyModerationBundle = useCallback(async () => {
    if (!snapshot) return;
    try {
      let moderation = null;
      let moderationError = null;
      if (isAdminViewer) {
        const { data, error } = await supabase.rpc('get_moderation_reports', { p_limit: 20 });
        if (error) moderationError = error.message;
        else moderation = Array.isArray(data) ? data : [];
      }
      const bundle = {
        generated_at: new Date().toISOString(),
        platform: Capacitor.getPlatform?.() || 'web',
        auth_user_id: snapshot.authUserId || null,
        app_user_id: snapshot.appUserId || null,
        notification_outbox: snapshot.outbox || [],
        push_queue: snapshot.pushQueue || [],
        daily_coin_earnings: snapshot.dailyCoinEarnings || [],
        streak_freeze_count: snapshot.streakFreezes ?? 0,
        moderation_reports: moderation,
        moderation_reports_error: moderationError,
      };
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      setActionMsg('Moderation bundle copied.');
    } catch (err) {
      setActionMsg(`Copy failed: ${err?.message || String(err)}`);
    }
  }, [snapshot, isAdminViewer]);

  return (
    <div
      className="fixed inset-0 z-[5] overflow-y-auto overflow-x-hidden overscroll-contain bg-slate-950 text-slate-100 p-4 sm:p-6"
      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
    >
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-black">Diagnostics</h1>
          <Link to="/" className="text-sm text-slate-300 hover:text-slate-100">Back</Link>
        </div>

        {!accessChecked ? (
          <div className="text-sm text-slate-400">Checking access...</div>
        ) : !canView ? (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm">
            Diagnostics is available only for moderation admins or when `localStorage.gweffit_debug=1`.
          </div>
        ) : (
          <>
            {actionMsg && <div className="text-xs sm:text-sm rounded-lg bg-slate-800 border border-slate-700 px-3 py-2">{actionMsg}</div>}

        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 space-y-1">
          {loading ? (
            <div className="text-sm text-slate-400">Loading...</div>
          ) : (
            <>
              <StatusRow label="Version" value={`${snapshot?.appInfo?.version} (${snapshot?.appInfo?.build})`} />
              <StatusRow label="Env" value={snapshot?.envName} />
              <StatusRow label="Debug Mode" value={snapshot?.debug ? 'true' : 'false'} />
              <StatusRow label="Auth User ID" value={snapshot?.authUserId} />
              <StatusRow label="App User ID" value={snapshot?.appUserId} />
              <StatusRow label="System Config ID" value={snapshot?.systemConfiguredId || 'not set'} />
              <StatusRow label="System Username" value={snapshot?.systemUsername} />
              <StatusRow label="System DB ID" value={snapshot?.dbSystemUserId || 'not found'} />
              <StatusRow label="System Resolved" value={`${snapshot?.systemResolved?.id || 'none'} (${snapshot?.systemResolved?.source || 'unknown'})`} />
              <StatusRow label="Push Permission" value={snapshot?.pushPermission} />
              <StatusRow label="push_enabled" value={String(snapshot?.pushEnabled)} />
              <StatusRow label="Active token count" value={snapshot?.activeTokenCount} />
              <StatusRow label="fcm_token_updated_at" value={snapshot?.fcmTokenUpdatedAt || '—'} />
              <StatusRow label="Prefs" value={JSON.stringify(snapshot?.prefs || {})} />
              <StatusRow label="Streak freezes" value={`${snapshot?.streakFreezes ?? 0} (${snapshot?.streakFreezesUpdatedAt || '—'})`} />
            </>
          )}
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
          <div className="text-sm font-semibold mb-3">Actions</div>
          <div className="flex flex-wrap gap-2">
            <button disabled={runningAction === 'refreshTokens'} onClick={refreshTokens} className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 px-3 py-2 text-xs font-semibold">Refresh tokens</button>
            <button onClick={() => navigate('/verdict-vault')} className="rounded-lg bg-slate-700 hover:bg-slate-600 px-3 py-2 text-xs font-semibold">Open Verdict Vault</button>
            <button disabled={runningAction === 'createFeatured'} onClick={createFeaturedTrial} className="rounded-lg bg-purple-700 hover:bg-purple-600 disabled:opacity-60 px-3 py-2 text-xs font-semibold">Create Featured Trial</button>
            <button disabled={!snapshot} onClick={copyDebugBundle} className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 px-3 py-2 text-xs font-semibold">Copy debug bundle</button>
            <button disabled={!snapshot} onClick={copyDebugSnapshot} className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 px-3 py-2 text-xs font-semibold">Copy debug snapshot</button>
            <button disabled={!snapshot} onClick={copyModerationBundle} className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 px-3 py-2 text-xs font-semibold">Copy moderation bundle</button>
            <button onClick={load} className="rounded-lg bg-slate-700 hover:bg-slate-600 px-3 py-2 text-xs font-semibold">Reload snapshot</button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
          <div className="text-sm font-semibold mb-3">Last 20 notification_outbox rows</div>
          <div className="space-y-2">
            {outboxRows.length === 0 && <div className="text-xs text-slate-400">No rows.</div>}
            {outboxRows.map((row, idx) => (
              <div key={`${row.created_at}_${idx}`} className="text-xs border border-slate-800 rounded-lg p-2">
                <div className="text-slate-200">{row.created_at} • {row.category} • {row.status}</div>
                <div className="text-slate-400">{row.failure_reason || '—'} • {row.dedupe_key || '—'}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
          <div className="text-sm font-semibold mb-3">Last 20 push_queue rows</div>
          <div className="space-y-2">
            {pushQueueRows.length === 0 && <div className="text-xs text-slate-400">No rows (or RLS hidden).</div>}
            {pushQueueRows.map((row, idx) => (
              <div key={`${row.created_at}_${idx}`} className="text-xs border border-slate-800 rounded-lg p-2">
                <div className="text-slate-200">{row.created_at} • {row.status || '—'}</div>
                <div className="text-slate-400">{row.dedupe_key || '—'} • {row.last_error || '—'}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
          <div className="text-sm font-semibold mb-3">Last 10 daily_coin_earnings rows</div>
          <div className="space-y-2">
            {coinRows.length === 0 && <div className="text-xs text-slate-400">No rows.</div>}
            {coinRows.map((row, idx) => (
              <div key={`${row.earning_date || row.created_at}_${idx}`} className="text-xs border border-slate-800 rounded-lg p-2">
                <div className="text-slate-200">{row.earning_date || '—'} • earned {row.earned ?? '—'}</div>
                <div className="text-slate-400">{row.updated_at || row.created_at || '—'}</div>
              </div>
            ))}
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
